// @flow
import { useSelector, useDispatch } from "react-redux";
import { login, logout, resetAuthErrors } from "../redux/actions";
import Cache from "../utils/browser/Cache";
import {
  getBasePath,
  getBasePathServer,
  INTERNAL_LOGIN_TYPE,
} from "../constants";
import { IllegalStateException } from "../exceptions";
import { Authenticate } from "../modularui";
import { useApplication } from "./useModularUIModel";

import type { ResetAuthErrorsAction } from "../redux/types";
import type { AuthenticationType } from "../models/types";

type LoginHook = {
  isAuthenticated: boolean,
  primaryAuthenticationType: ?AuthenticationType,
  authenticationTypes: Array<AuthenticationType>,
  errorMessage: ?string,
  resetErrors: () => ResetAuthErrorsAction,
  login: (username: string, password: string) => any, // Changed to any for Promise support
  redirectLogin: (authenticationType: AuthenticationType) => void,
};

type LogoutHook = {
  isAuthenticated: boolean,
  primaryAuthenticationType: ?AuthenticationType,
  authenticationTypes: Array<AuthenticationType>,
  logout: () => any, // Changed to any for Promise support
  logoutUrl: string,
};

/**
 * Hook for handling login logic
 */
export const useLogin = (): LoginHook => {
  Cache.setItem("isRedirectLogin", false);

  const dispatch = useDispatch();
  const application = useApplication();
  const auth = useSelector((state) => state.auth);

  const isAuthenticated = application?.isLoggedIn ?? false;
  const cachedType = Cache.getItem("primaryAuthenticationType");

  return {
    isAuthenticated,
    // Safely fallback to the first available type if nothing is cached
    primaryAuthenticationType:
      cachedType ?? application?.authenticationTypes?.[0],
    authenticationTypes: application?.authenticationTypes ?? [],
    errorMessage: auth.error,
    resetErrors: () => dispatch(resetAuthErrors()),
    login: (username: string, password: string) =>
      dispatch(login(username, password)),
    redirectLogin: (authenticationType: AuthenticationType) => {
      if (Cache.getItem("isRedirectLogin")) {
        throw new IllegalStateException(
          `Could not redirect to '${authenticationType.authentication}' using url: '${authenticationType.redirectUri ?? ""}'`,
        );
      }

      if (!isAuthenticated) {
        Cache.setItem("isRedirectLogin", true);
        // $FlowIssue
        globalThis.location.assign(
          `${getBasePathServer()}${authenticationType.redirectUri ?? ""}`,
        );
      }
    },
  };
};

/**
 * Hook for handling logout logic
 */
export const useLogout = (): LogoutHook => {
  const dispatch = useDispatch();
  const application = useApplication();
  const BASE_PATH = getBasePath();
  const authenticate = new Authenticate();

  const isAuthenticated = application?.isLoggedIn ?? false;
  const cachedType = Cache.getItem("primaryAuthenticationType");

  return {
    isAuthenticated,
    primaryAuthenticationType:
      cachedType ?? application?.authenticationTypes?.[0],
    authenticationTypes: application?.authenticationTypes ?? [],
    logout: () => {
      const type = authenticate.authenticationType;

      if (type === INTERNAL_LOGIN_TYPE.PAC4J_INDIRECT) {
        authenticate.redirectToLogout();
        return; // Redirects stop execution
      }

      if (Cache.getItem("isRedirectLogin")) {
        // $FlowIssue
        const origin = globalThis.location.origin;
        const url = `${authenticate.getLogoutUrl()}?url=${origin}${BASE_PATH}`;
        Cache.setItem("isRedirectLogin", false);
        // $FlowIssue
        globalThis.location.assign(url);
        return;
      }

      // Returns the Promise from the logout thunk
      return dispatch(logout());
    },
    logoutUrl: authenticate.getLogoutUrl(),
  };
};
